import { useEffect, useState } from "react";

import { useAuth } from "src/hooks/use-auth";
import { useRouter } from "src/hooks/use-router";
import { sendVerifyEmailEmail } from "src/utils/snug/snugAuthApi";
import { SignupStepper } from "src/sections/starter-pay/signup-stepper";
import { readStarterPayContext } from "src/sections/starter-pay/context";
import {
  readCustomPlanParams,
  customPlanQueryString,
} from "src/sections/starter-pay/custom-plan-storage";
import "./starter-pay.css";

const isGetSnugDomain =
  window.location.hostname.endsWith("getsnug.com") ||
  window.location.hostname === "localhost";

const REDIRECT_BASE = "/get-started";

// Append any stashed custom plan params so a customer who clicks the email
// link on a different device still gets the right pricing.
function buildRedirectPath(): string {
  return REDIRECT_BASE + customPlanQueryString(readCustomPlanParams());
}

export default function GetStartedVerifyPage() {
  const router = useRouter();
  const { user } = useAuth();
  const [email, setEmail] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const ctx = readStarterPayContext();
    // Use context email first; fall back to auth user email for cross-device
    // flows where localStorage context is absent.
    const resolvedEmail = ctx?.email || user?.username || null;
    if (!resolvedEmail) {
      router.replace("/get-started");
      return;
    }
    setEmail(resolvedEmail);
    sendVerifyEmailEmail(resolvedEmail, false, buildRedirectPath())
      .then(() => setSent(true))
      .catch(() => setError("Could not send verification email. Please try again."));
  }, [router, user]);

  if (!isGetSnugDomain) return null;

  const handleResend = async () => {
    if (!email || resending) return;
    setResending(true);
    setError("");
    try {
      await sendVerifyEmailEmail(email, false, buildRedirectPath());
      setSent(true);
    } catch {
      setError("Could not resend verification email. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="sp-page">
      <div className="sp-brand">
        <img
          src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/snug_logo.png"
          alt="Snug"
          className="sp-brand-logo"
        />
      </div>

      <section className="sp-inner">
        <h1 className="sp-headline">
          Get started with <em>Snug.</em>
        </h1>

        <p className="sp-sub">
          Includes full platform access, white-label setup, and onboarding
          support.
        </p>

        <SignupStepper currentStep="account" />

        <div className="sp-card" style={{ textAlign: "center", padding: "2.5rem 2rem" }}>
          <img
            src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/inbox.png"
            alt=""
            style={{ maxWidth: 48, marginBottom: "1rem" }}
          />
          <h2 className="sp-section-title" style={{ marginBottom: "0.5rem" }}>
            Check your inbox
          </h2>
          <p className="sp-section-help">
            We sent a verification link to{" "}
            <strong>{email}</strong>.
            <br />
            Click the link in the email to continue to payment.
          </p>
          <p className="sp-section-help" style={{ marginTop: "0.5rem", fontSize: "0.8rem" }}>
            Can't find it? Check your spam folder.
          </p>
        </div>

        {error && <div className="sp-error">{error}</div>}

        <button
          type="button"
          className="sp-cta"
          style={{ background: "transparent", color: "#3045C9", border: "1.5px solid #3045C9", marginTop: "0.5rem" }}
          disabled={resending}
          onClick={handleResend}
        >
          {resending ? <span className="sp-cta-spinner" aria-hidden="true" /> : null}
          {resending ? "Sending…" : sent ? "Resend verification email" : "Send verification email"}
        </button>

        <p className="sp-disclaimer">
          The link expires after 24 hours. If you have trouble, contact{" "}
          <a href="mailto:support@getsnug.com">support@getsnug.com</a>.
        </p>
      </section>
    </div>
  );
}
